import { FC, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import './filterDatePicker.css';
import { capitalizeFirstLetter, getRange } from '../../utils';
import { ChevronLeft, ChevronRight } from '../../assets/icons';

const startYear = 1990;

export enum DateType {
  START = 'start',
  END = 'end',
}
export interface DateFilters {
  [DateType.START]: Date | null;
  [DateType.END]: Date | null;
}

interface DateFilterProp {
  model: DateFilters;
  onModelUpdate: (update: DateFilters) => void;
  maxDate?: Date;
  minDate?: Date;
  options: {
    title?: string;
    startTitle: string;
    endTitle: string;
    datePlaceHolderText: string;
  };
}

const DateFilter: FC<DateFilterProp> = ({ options, model, onModelUpdate, maxDate, minDate }) => {
  const fromCalenderRef = useRef<DatePicker<never, undefined> | null>(null);
  const toCalenderRef = useRef<DatePicker<never, undefined> | null>(null);
  const openDatepicker = (type: DateType) => {
    if (type === DateType.START) fromCalenderRef.current?.setOpen(true);
    if (type === DateType.END) toCalenderRef.current?.setOpen(true);
  };

  const { title, startTitle, endTitle, datePlaceHolderText } = options;
  const dateConfig = useMemo(
    () => [
      {
        type: DateType.START,
        title: `${capitalizeFirstLetter(startTitle)}`,
        ref: fromCalenderRef,
      },
      {
        type: DateType.END,
        title: `${capitalizeFirstLetter(endTitle)}`,
        ref: toCalenderRef,
      },
    ],
    [startTitle, endTitle],
  );

  const months = useMemo(() => moment.months(), []);
  const years: number[] = useMemo(
    () => getRange(moment().year() - startYear + 1).map((x: number) => startYear + x),
    [],
  );

  return (
    <div className="p-4">
      {title && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {title}
          </span>
          {(model[DateType.START] || model[DateType.END]) && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange/10 dark:bg-teal-500/10 text-orange dark:text-teal-400 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-orange dark:bg-teal-400" />
              Active
            </span>
          )}
        </div>
      )}

      {/* Date Range Selector */}
      <div className="flex gap-3 mb-4">
        {dateConfig.map((x) => (
          <button
            key={`${x.type}`}
            onClick={() => openDatepicker(x.type)}
            className={`flex-1 p-3 rounded-xl border-2 transition-all duration-200 ${
              model[x.type]
                ? 'border-orange dark:border-teal-500 bg-orange/5 dark:bg-teal-500/10'
                : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 hover:border-gray-300 dark:hover:border-slate-500'
            }`}
          >
            <span className="block text-xs font-semibold text-orange dark:text-teal-400 mb-1">{x.title}</span>
            <span
              className={`block text-sm font-medium ${
                model[x.type] ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {!model[x.type]
                ? `${capitalizeFirstLetter(datePlaceHolderText)}`
                : moment(model[x.type]).format('DD MMM YYYY')}
            </span>
          </button>
        ))}
      </div>

      {/* Calendar */}
      <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4">
        <DatePicker
          maxDate={maxDate}
          minDate={minDate}
          dayClassName={(date: Date) =>
            moment(date).isBetween(moment(model[DateType.START]), moment(model[DateType.END])) ||
            moment(date).isSame(moment(model[DateType.START])) ||
            moment(date).isSame(moment(model[DateType.END]))
              ? 'bg-orange dark:bg-teal-500 text-white rounded-lg'
              : ''
          }
          weekDayClassName={() => 'hidden'}
          calendarClassName="!border-none rounded-none w-full dark-calendar"
          onChange={(dates) => {
            const [start, end] = dates;
            onModelUpdate({ [DateType.START]: start, [DateType.END]: end });
          }}
          startDate={model[DateType.START]}
          endDate={model[DateType.END]}
          calendarStartDay={6}
          selectsRange
          inline
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="flex items-center justify-between pb-4">
              <button
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 transition-colors"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              <div className="flex items-center gap-2">
                <select
                  className="text-sm font-semibold outline-none bg-transparent text-gray-800 dark:text-gray-200 cursor-pointer hover:text-orange dark:hover:text-teal-400 transition-colors"
                  value={months.find((x) => x === moment(date).format('MMMM'))}
                  onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                >
                  {months.map((option) => (
                    <option key={option} value={option} className="bg-white dark:bg-slate-800">
                      {option}
                    </option>
                  ))}
                </select>
                <select
                  className="text-sm font-semibold outline-none bg-transparent text-gray-800 dark:text-gray-200 cursor-pointer hover:text-orange dark:hover:text-teal-400 transition-colors"
                  value={moment(date).year()}
                  onChange={({ target: { value } }) => changeYear(Number(value))}
                >
                  {years.map((option) => (
                    <option key={option} value={option} className="bg-white dark:bg-slate-800">
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 transition-colors"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default DateFilter;
